import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { apiFetch } from '../lib/api'
import { boundedAuthConfig, providerLogoutUrlOf } from '../lib/authConfig'

/** The sign-out both the AppShell and the account step (§58) run, after any
 * confirm of their own: end the sign-in on the server, then leave through
 * the Cognito logout URL — or, with none, go to `fallbackPath`.
 *
 * Only POST /logout clears the HttpOnly session, refresh and pending-login
 * cookies and revokes the refresh token (it needs no valid session, so an
 * expired one still signs out). A dropped request would leave the refresh
 * cookie able to renew this user's session for whoever opens the portal next
 * on this machine, so a failure is retried once. It is sent at once, not
 * after the config: the config is normally answered from the cache, but an
 * uncached read can take up to LOGOUT_CONFIG_TIMEOUT_MS, and a tab closed in
 * that time must not leave the cookies behind.
 *
 * Leaving matters as much: the portal session is gone, but Cognito's own
 * managed-login session would sign the user straight back in (MS-977). */
export function useSignOut(fallbackPath: string): () => Promise<void> {
  const { logout } = useAuth()
  const navigate = useNavigate()
  return useCallback(async () => {
    const postLogout = () => apiFetch('/api/v1/auth/logout', { method: 'POST' })
    const [config] = await Promise.all([
      boundedAuthConfig(),
      postLogout().catch(postLogout).catch(() => {}),
    ])
    const leaveTo = providerLogoutUrlOf(config)
    logout(leaveTo ? 'provider_logout' : 'logout')
    if (leaveTo) {
      window.location.assign(leaveTo)
      return
    }
    navigate(fallbackPath, { replace: true })
  }, [fallbackPath, logout, navigate])
}
